import type { DeployAgent, Project, ProjectRolloutView } from "@szyos/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useId, useState } from "react";
import { api, errorMessage } from "../api";
import { Icon } from "../icons";
import { authQuery } from "../pages/Login";
import { HoldButton } from "./HoldButton";
import { ErrorNote, useToast } from "./feedback";

const isActive = (v: ProjectRolloutView | undefined) =>
  v?.last?.state === "requested" || v?.last?.state === "running";

const short = (sha: string | null) => (sha ? sha.slice(0, 7) : "unbekannt");

/** Helfer verbinden: einmaliger Code und die Befehle für den App-Server. */
function ConnectHelper() {
  const toast = useToast();
  const [code, setCode] = useState<{ code: string; expiresAt: string } | null>(null);
  const create = useMutation({
    mutationFn: api.createDeployAgentCode,
    onSuccess: (c) => {
      setCode(c);
      toast("Code erstellt. Er gilt 30 Minuten.");
    },
    onError: (err) => toast(errorMessage(err), "error"),
  });
  if (!code) {
    return (
      <button type="button" className="btn" onClick={() => create.mutate()} disabled={create.isPending}>
        <span className="btn-label">
          <Icon name="key" /> Helfer verbinden
        </span>
      </button>
    );
  }
  return (
    <div className="rollout-connect" role="status">
      <p>
        Code, gültig bis{" "}
        {new Date(code.expiresAt).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}:{" "}
        <code>{code.code}</code>
      </p>
      <ol>
        <li>Per PuTTY als root auf dem App-Server anmelden.</li>
        <li>
          <code>szyos-deployer register</code> ausführen und den Code einfügen.
        </li>
        <li>
          <code>systemctl enable --now szyos-deployer</code>, danach <code>szyos-deployer check</code>.
        </li>
      </ol>
    </div>
  );
}

/** Ziel wählen: welcher Helfer, welche App aus seiner Liste. */
function TargetForm({ project, agents }: { project: Project; agents: DeployAgent[] }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const ids = { agent: useId(), app: useId() };
  const usable = agents.filter((a) => !a.revoked);
  const [agentId, setAgentId] = useState(usable[0]?.id ?? "");
  const agent = usable.find((a) => a.id === agentId);
  const [app, setApp] = useState(agent?.apps[0]?.key ?? "");
  const save = useMutation({
    mutationFn: () => api.setDeployTarget(project.id, { agentId, app }),
    onSuccess: async (p) => {
      queryClient.setQueryData(["project", project.id], p);
      await queryClient.invalidateQueries({ queryKey: ["project-rollout", project.id] });
      toast(`Ausrollziel gespeichert: ${app}`);
    },
    onError: (err) => toast(errorMessage(err), "error"),
  });
  if (usable.length === 0) return null;
  return (
    <form
      className="form rollout-target"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <label htmlFor={ids.agent}>Helfer</label>
      <select
        id={ids.agent}
        value={agentId}
        onChange={(e) => {
          setAgentId(e.target.value);
          setApp(usable.find((a) => a.id === e.target.value)?.apps[0]?.key ?? "");
        }}
      >
        {usable.map((a) => (
          <option key={a.id} value={a.id}>
            {a.name} ({a.online ? "erreichbar" : "meldet sich nicht"})
          </option>
        ))}
      </select>
      <label htmlFor={ids.app}>App aus seiner Liste</label>
      <select id={ids.app} value={app} onChange={(e) => setApp(e.target.value)}>
        {(agent?.apps ?? []).map((a) => (
          <option key={a.key} value={a.key}>
            {a.key}
          </option>
        ))}
      </select>
      <button type="submit" className="btn btn--primary" disabled={!agentId || !app || save.isPending}>
        <span className="btn-label">Ziel speichern</span>
      </button>
    </form>
  );
}

function StateLine({ view }: { view: ProjectRolloutView }) {
  const last = view.last;
  if (!last) return null;
  if (last.state === "done") {
    return (
      <p className="rollout-state rollout-state--ok">
        <Icon name="check" /> Ausgerollt: {last.message}
      </p>
    );
  }
  if (last.state === "failed") {
    return (
      <p className="rollout-state rollout-state--error" role="alert">
        <Icon name="alert" /> Ausrollen abgebrochen: {last.message}
      </p>
    );
  }
  return (
    <p className="rollout-state">
      <Icon name="clock" /> Wird ausgerollt: {last.message}
    </p>
  );
}

/**
 * Ausrollen per Knopf auf der Projektseite (Pilot RadaR, 24.10.2026). Ausführen tut der Helfer auf dem App-Server;
 * das Studio zeigt, was er meldet: Stand des Checkouts, Fortschritt und die letzten Zeilen der Ausgabe.
 */
export function ProjectRolloutPanel({ project }: { project: Project }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const hintId = useId();
  const [error, setError] = useState<string | null>(null);
  const auth = useQuery(authQuery);
  const isOwner = auth.data?.user?.role === "owner";
  const rollout = useQuery({
    queryKey: ["project-rollout", project.id],
    queryFn: () => api.projectRollout(project.id),
    refetchInterval: (q) => (isActive(q.state.data) ? 4000 : 30_000),
  });
  const agents = useQuery({ queryKey: ["deploy-agents"], queryFn: api.deployAgents, enabled: isOwner });
  const start = useMutation({
    mutationFn: (sha: string) => api.requestProjectRollout(project.id, sha),
    onSuccess: (view) => {
      setError(null);
      queryClient.setQueryData(["project-rollout", project.id], view);
      toast("Ausrollen gestartet. Der Helfer übernimmt in wenigen Sekunden.");
    },
    onError: (err) => {
      setError(errorMessage(err));
      toast(errorMessage(err), "error");
    },
  });
  const clear = useMutation({
    mutationFn: () => api.setDeployTarget(project.id, { agentId: null, app: null }),
    onSuccess: async (p) => {
      queryClient.setQueryData(["project", project.id], p);
      await queryClient.invalidateQueries({ queryKey: ["project-rollout", project.id] });
      toast("Ausrollziel entfernt.");
    },
    onError: (err) => toast(errorMessage(err), "error"),
  });
  const view = rollout.data;
  if (!view) return null;

  return (
    <section className="panel panel--section rollout-panel" aria-labelledby="projectRolloutHead">
      <div className="panel-head">
        <h2 id="projectRolloutHead">
          <Icon name="rocket" /> Ausrollen per Knopf
        </h2>
        <span className="muted" role="status">
          {isActive(view)
            ? "Wird ausgerollt"
            : view.configured
              ? view.agent?.online
                ? "Helfer erreichbar"
                : "Helfer meldet sich nicht"
              : "Nicht eingerichtet"}
        </span>
      </div>
      {!view.configured && (
        <>
          <p className="muted">
            Ein Helfer auf dem App-Server rollt auf Knopfdruck genau den Stand von main aus. Er führt nur
            Befehle aus seiner festen Liste auf dem Server aus.
          </p>
          {isOwner && (
            <TargetForm
              key={(agents.data ?? []).map((a) => `${a.id}:${a.apps.map((x) => x.key).join(",")}`).join("|")}
              project={project}
              agents={agents.data ?? []}
            />
          )}
          {isOwner && <ConnectHelper />}
        </>
      )}
      {view.configured && (
        <>
          <p className="muted">
            Helfer <strong>{view.agent?.name}</strong>, App <code>{view.app}</code>. Auf dem Server läuft{" "}
            <code>{short(view.liveSha)}</code>, auf main liegt <code>{short(view.mainSha)}</code>.
          </p>
          {view.ahead.length > 0 && (
            <ul className="rollout-list">
              {view.ahead.map((c) => (
                <li key={c.sha}>
                  <code>{c.sha.slice(0, 7)}</code> {c.title}
                </li>
              ))}
              {view.aheadTotal > view.ahead.length && (
                <li className="muted">und {view.aheadTotal - view.ahead.length} weitere</li>
              )}
            </ul>
          )}
          <StateLine view={view} />
          {view.last?.log && (
            <details className="rollout-log">
              <summary>Ausgabe des letzten Laufs</summary>
              <pre>{view.last.log}</pre>
            </details>
          )}
          {isOwner && !isActive(view) && view.mainSha && (
            <div className="rollout-actions">
              <HoldButton
                disabled={!view.ready || start.isPending}
                describedBy={hintId}
                onConfirm={() => start.mutate(view.mainSha ?? "")}
                onTooShort={() => toast("Zum Ausrollen gedrückt halten.")}
              >
                {start.isPending ? "Wird übergeben …" : "Jetzt ausrollen"}
              </HoldButton>
              <span id={hintId} className="muted">
                {view.ready
                  ? "Gedrückt halten. Der Helfer rollt genau diesen Stand aus."
                  : view.blockers.join(" ")}
              </span>
            </div>
          )}
          {isOwner && !view.mainSha && <p className="muted">{view.blockers.join(" ")}</p>}
          {isOwner && !isActive(view) && (
            <button
              type="button"
              className="chip-btn"
              onClick={() => clear.mutate()}
              disabled={clear.isPending}
            >
              <Icon name="x" /> Ausrollziel entfernen
            </button>
          )}
        </>
      )}
      <ErrorNote error={error} />
    </section>
  );
}
